import type { UploadResult } from '../../shared/types';

export class ApiError extends Error {
  constructor(
    public readonly status: number,
    message: string,
    public readonly code?: string,
  ) {
    super(message);
  }
}

let shareToken: string | null = null;

/** Auf Freigabe-Links schickt jede Anfrage das Link-Token mit. */
export function setShareToken(token: string | null): void {
  shareToken = token;
}

export function getShareToken(): string | null {
  return shareToken;
}

/** Query-Keys trennen Agentur- und Link-Ansicht, damit sich Caches nicht vermischen. */
export const qk = (...parts: unknown[]): unknown[] => (shareToken ? ['share', shareToken, ...parts] : parts);

/** Für direkte Links (Downloads), bei denen keine Header gesetzt werden können. */
export function withShare(url: string): string {
  if (!shareToken) return url;
  return `${url}${url.includes('?') ? '&' : '?'}share=${encodeURIComponent(shareToken)}`;
}

function headers(json: boolean): Record<string, string> {
  const h: Record<string, string> = { 'X-Requested-With': 'zeigbanner' };
  if (json) h['Content-Type'] = 'application/json';
  if (shareToken) h['X-Share-Token'] = shareToken;
  return h;
}

async function toError(res: Response): Promise<ApiError> {
  let data: { error?: string; code?: string } = {};
  try {
    data = await res.json();
  } catch {
    // keine JSON-Antwort
  }
  return new ApiError(res.status, data.error ?? `Fehler ${res.status}`, data.code);
}

export async function api<T>(
  path: string,
  options: { method?: string; body?: unknown; signal?: AbortSignal } = {},
): Promise<T> {
  const res = await fetch(path, {
    method: options.method ?? 'GET',
    headers: headers(options.body !== undefined),
    body: options.body !== undefined ? JSON.stringify(options.body) : undefined,
    credentials: 'same-origin',
    signal: options.signal,
  });
  if (!res.ok) throw await toError(res);
  if (res.status === 204) return undefined as T;
  return (await res.json()) as T;
}

export const post = <T>(path: string, body: unknown = {}) => api<T>(path, { method: 'POST', body });
export const patch = <T>(path: string, body: unknown) => api<T>(path, { method: 'PATCH', body });
export const del = <T>(path: string) => api<T>(path, { method: 'DELETE' });

export interface UploadOptions {
  bannerId?: string;
  /** Zielordner in der Kampagne. */
  folderId?: string;
  note?: string;
  hints?: Record<string, { width?: number; height?: number; durationMs?: number }>;
  /** Ordner-Upload: relativer Pfad je Datei, in derselben Reihenfolge wie `files`. */
  paths?: string[];
  onProgress?: (fraction: number) => void;
}

/** Upload mit Fortschrittsanzeige (fetch kann das nicht, daher XHR). */
export function uploadFiles(
  campaignId: string,
  files: File[],
  options: UploadOptions = {},
): Promise<UploadResult[]> {
  return new Promise((resolve, reject) => {
    // Felder vor den Dateien – der Server liest den Strom der Reihe nach.
    const form = new FormData();
    if (options.bannerId) form.append('bannerId', options.bannerId);
    if (options.folderId) form.append('folderId', options.folderId);
    if (options.note) form.append('note', options.note);
    if (options.hints) form.append('hints', JSON.stringify(options.hints));
    if (options.paths) form.append('paths', JSON.stringify(options.paths));
    for (const file of files) form.append('file', file, file.name);
    const xhr = new XMLHttpRequest();
    xhr.open('POST', `/api/campaigns/${campaignId}/upload`);
    xhr.setRequestHeader('X-Requested-With', 'zeigbanner');
    xhr.upload.onprogress = (e) => {
      if (e.lengthComputable) options.onProgress?.(e.loaded / e.total);
    };
    xhr.onload = () => {
      let data: { results?: UploadResult[]; error?: string } = {};
      try {
        data = JSON.parse(xhr.responseText);
      } catch {
        // leer
      }
      if (xhr.status >= 200 && xhr.status < 300 && data.results) resolve(data.results);
      else reject(new ApiError(xhr.status, data.error ?? 'Upload fehlgeschlagen'));
    };
    xhr.onerror = () => reject(new ApiError(0, 'Keine Verbindung zum Server.'));
    xhr.send(form);
  });
}

/** Datei für das Erscheinungsbild (Logo, Favicon, Schrift) hochladen; liefert die neuen Einstellungen. */
export async function uploadBrandAsset<T>(slot: string, file: File): Promise<T> {
  const form = new FormData();
  form.append('file', file, file.name);
  const res = await fetch(`/api/settings/branding/assets/${slot}`, {
    method: 'POST',
    headers: { 'X-Requested-With': 'zeigbanner' },
    body: form,
  });
  if (!res.ok) throw await toError(res);
  return (await res.json()) as T;
}

export async function uploadLogo(
  clientId: string,
  file: File,
  slot: 'logo' | 'presenter' = 'logo',
): Promise<void> {
  const form = new FormData();
  form.append('file', file, file.name);
  const res = await fetch(`/api/clients/${clientId}/logo${slot === 'presenter' ? '?slot=presenter' : ''}`, {
    method: 'POST',
    headers: { 'X-Requested-With': 'zeigbanner' },
    body: form,
  });
  if (!res.ok) throw await toError(res);
}
